// Centralized environment configuration
import isElectron, { isWeb, isDevelopment, isProduction } from '../Utils/isElectron';

// Environment detection
export const environment = {
  isElectron: isElectron(),
  isWeb: isWeb(),
  isDevelopment: isDevelopment(),
  isProduction: isProduction(),
  platform: isElectron() ? 'electron' : 'web'
};

// Environment-specific configurations
export const config = {
  // Firebase configuration
  firebase: {
    useAdminSDK: environment.isElectron,
    useClientSDK: environment.isWeb,
    realTimeListeners: true, // Both environments support this
  },
  
  // Update system configuration
  updates: {
    enabled: environment.isElectron,
    autoCheck: environment.isElectron,
    webFallback: environment.isWeb,
  },
  
  // Service worker configuration
  serviceWorker: {
    enabled: environment.isWeb && environment.isProduction,
    autoRegister: false, // We handle this manually
  },
  
  // Authentication configuration
  auth: {
    useMainProcess: environment.isElectron,
    useFirebaseAuth: environment.isWeb,
    oauthRedirect: environment.isWeb,
  },
  
  // Storage configuration
  storage: {
    useElectronStorage: environment.isElectron,
    useLocalStorage: environment.isWeb,
    useFirestore: true, // Both environments
  },
  
  // Real-time features
  realtime: {
    events: true, // Both environments support real-time events
    collaboration: false, // Liveblocks disabled
    cursors: false, // Liveblocks disabled
  }
};

// Environment-specific feature flags
export const features = {
  // Only available in Electron
  electronOnly: {
    nativeUpdates: true,
    mainProcessFirebase: true,
    windowControls: true,
    fileSystemAccess: true,
    secureStorage: true,
  },
  
  // Only available in Web
  webOnly: {
    serviceWorker: true,
    pwaInstall: true,
    browserAuth: true,
    pushNotifications: true,
  },
  
  // Available in both
  universal: {
    realTimeEvents: true,
    firebaseCRUD: true,
    userSettings: true,
    collaboration: false, // Liveblocks disabled
    localStorage: true,
    userPreferences: true,
  }
};

// Service capabilities per environment
export const serviceCapabilities = {
  firebase: {
    electron: {
      adminSDK: true,
      realTimeListeners: true,
      offlineSupport: true,
      secureOperations: true
    },
    web: {
      clientSDK: true,
      realTimeListeners: true,
      offlineSupport: false,
      secureOperations: false
    }
  },
  
  auth: {
    electron: {
      mainProcess: true,
      secureStorage: true,
      oauthFlow: true,
      persistentSessions: true
    },
    web: {
      firebaseAuth: true,
      browserStorage: true,
      oauthFlow: true,
      persistentSessions: true
    }
  },
  
  storage: {
    electron: {
      localStorage: true,
      secureStorage: true,
      fileSystem: true,
      userPreferences: true
    },
    web: {
      localStorage: true,
      sessionStorage: true,
      indexedDB: true,
      userPreferences: true
    }
  },
  
  updates: {
    electron: {
      nativeUpdates: true,
      autoCheck: true,
      backgroundDownload: true,
      rollbackSupport: true
    },
    web: {
      serviceWorker: true,
      autoReload: true,
      cacheManagement: true,
      versionControl: true
    }
  }
};

// Helper functions
export const isFeatureEnabled = (featurePath) => {
  const parts = featurePath.split('.');
  let current = features;
  
  for (const part of parts) {
    if (current[part] === undefined) {
      return false;
    }
    current = current[part];
  }
  
  return current === true;
};

export const getServiceCapability = (service, capability) => {
  if (serviceCapabilities[service] && serviceCapabilities[service][environment.platform]) {
    return serviceCapabilities[service][environment.platform][capability] || false;
  }
  return false;
};

export const getEnvironmentCapabilities = () => {
  const capabilities = {};
  
  Object.keys(serviceCapabilities).forEach(service => {
    capabilities[service] = serviceCapabilities[service][environment.platform] || {};
  });
  
  return capabilities;
};

// Log environment info in development
if (environment.isDevelopment) {
  console.log('[Environment] Configuration loaded:', {
    environment: environment.platform,
    config: config,
    features: features,
    serviceCapabilities: getEnvironmentCapabilities()
  });
}

export default environment;
